import { AnyAction, Store } from 'redux';
import { PersistConfig, Persistor, persistReducer, persistStore } from 'redux-persist';
import {
    appDrawerReducer,
    localeReducer,
    serviceWorkerReducer,
    socketReducer,
} from 'src/store/modules';

import { AppDispatch } from 'src/types/reduxHelpers';
import { InterviewState } from 'src/store/state/interviewState';
import { InvitationState } from 'src/store/state/invitationState';
import { ThemeState } from 'src/store/state/themeState';
import { combineReducers } from 'redux';
import { configureStore as configureReduxStore } from '@reduxjs/toolkit';
import { interviewReducer } from 'src/store/modules/interviewModule';
import { invitationReducer } from 'src/store/modules/invitationModule';
import { nameof } from 'ts-simple-nameof';
import storage from 'redux-persist/lib/storage';
import { themeReducer } from 'src/store/modules/themeModule';
import thunk from 'redux-thunk';

const themePersistConfig: PersistConfig<ThemeState> = {
    key: 'exit-interview:theme',
    storage: storage,
    whitelist: [
        nameof<ThemeState>(x => x.isDark),
        nameof<ThemeState>(x => x.primaryColor),
    ],
};

const invitationPersistConfig: PersistConfig<InvitationState> = {
    key: 'exit-interview:invitation',
    storage: storage,
    whitelist: [
        nameof<InvitationState>(x => x.token),
        nameof<InvitationState>(x => x.employee),
    ],
};

const interviewPersistConfig: PersistConfig<InterviewState> = {
    key: 'exit-interview:interview',
    storage: storage,
    blacklist: [],
};

const makeRootReducer = combineReducers({
    drawer: appDrawerReducer,
    interview: persistReducer(interviewPersistConfig, interviewReducer),
    invitation: persistReducer(invitationPersistConfig, invitationReducer),
    locale: localeReducer,
    serviceWorker: serviceWorkerReducer,
    socket: socketReducer,
    theme: persistReducer(themePersistConfig, themeReducer),
});

export type RootState = ReturnType<typeof makeRootReducer>;

export function configureStore(initialState?: RootState): { store: Store<RootState, AnyAction> & { dispatch: AppDispatch }, persistor: Persistor } {
    const persistedReducer = makeRootReducer;

    const store = configureReduxStore({
        reducer: persistedReducer,
        preloadedState: initialState,
        middleware: [thunk],
        devTools: process.env.NODE_ENV === 'development',
    });

    const persistor = persistStore(store);

    return { store, persistor };
}